
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { 
  Search, 
  RefreshCw, 
  Edit,
  Save,
  X,
  File
} from "lucide-react";

interface PageContent {
  id: string;
  page_name: string;
  title: string;
  content: string;
  meta_description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const PagesModule: React.FC = () => {
  const [pages, setPages] = useState<PageContent[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Fields for editing
  const [editedTitle, setEditedTitle] = useState<string>("");
  const [editedContent, setEditedContent] = useState<string>("");
  const [editedMetaDescription, setEditedMetaDescription] = useState<string>("");

  // Load pages from Supabase
  const loadPages = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .order('page_name', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      setPages(data || []);
    } catch (error) {
      console.error("Error loading pages:", error);
      toast.error("Erro ao carregar páginas institucionais");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, []);
  
  // Filter pages based on search term
  const filteredPages = pages.filter(page => {
    return (
      page.page_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (page.content && page.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (page.meta_description && page.meta_description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  // Start editing a page
  const handleEdit = (page: PageContent) => {
    setEditingId(page.id);
    setEditedTitle(page.title);
    setEditedContent(page.content);
    setEditedMetaDescription(page.meta_description || "");
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedTitle("");
    setEditedContent("");
    setEditedMetaDescription("");
  };

  // Save edited page
  const handleSave = async (id: string) => {
    if (!editedTitle.trim() || !editedContent.trim()) {
      toast.error("Título e conteúdo são obrigatórios");
      return;
    }

    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('pages')
        .update({ 
          title: editedTitle,
          content: editedContent,
          meta_description: editedMetaDescription || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      toast.success("Página atualizada com sucesso");
      loadPages();
      setEditingId(null);
      setEditedTitle("");
      setEditedContent("");
      setEditedMetaDescription("");
    } catch (error) {
      console.error("Error updating page:", error);
      toast.error("Erro ao atualizar página");
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to get friendly page name
  const getFriendlyPageName = (pageName: string) => {
    const pageNames: Record<string, string> = {
      'about': 'Sobre Nós',
      'contact': 'Contato',
      'privacy-policy': 'Política de Privacidade',
      'terms-of-service': 'Termos de Serviço',
      'faq': 'FAQ',
      'help-center': 'Central de Ajuda'
    };
    
    return pageNames[pageName] || pageName;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-toctoc-blue"></div>
        <span className="ml-3">Carregando páginas institucionais...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Páginas Institucionais</h1>
          <p className="text-gray-500">Edição do conteúdo das páginas institucionais</p>
        </div>
        <div className="mt-4 md:mt-0 flex gap-4">
          <Button 
            variant="outline"
            onClick={loadPages}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>
      
      {/* Search */}
      <div className="flex items-center mb-6 bg-white rounded-lg shadow p-2">
        <Search className="w-5 h-5 text-gray-400 ml-2" />
        <Input
          className="border-0 focus-visible:ring-0"
          placeholder="Buscar páginas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      {/* Pages List */}
      {filteredPages.length === 0 ? (
        <div className="bg-white p-12 rounded-lg shadow text-center">
          <File className="w-16 h-16 text-gray-300 mx-auto" />
          <p className="text-gray-500 mt-4">
            {searchTerm ? "Nenhuma página encontrada com esse termo de busca" : "Nenhuma página disponível"}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredPages.map(page => (
            <Card key={page.id} className="overflow-hidden">
              {editingId === page.id ? (
                <CardContent className="p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Página</label>
                      <Input
                        value={getFriendlyPageName(page.page_name)}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Título</label>
                      <Input
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        placeholder="Título da página"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Descrição Meta (SEO)</label>
                      <Input
                        value={editedMetaDescription}
                        onChange={(e) => setEditedMetaDescription(e.target.value)}
                        placeholder="Descrição para mecanismos de busca"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Texto exibido nos resultados de pesquisa no Google e outras buscas
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Conteúdo</label>
                      <Textarea
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        placeholder="Conteúdo da página"
                        rows={12}
                        className="min-h-[300px]"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={handleCancelEdit}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancelar
                      </Button>
                      <Button
                        className="bg-toctoc-blue hover:bg-blue-700"
                        onClick={() => handleSave(page.id)}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Salvando...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              ) : (
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row justify-between">
                    <div>
                      <div className="flex items-center">
                        <File className="w-5 h-5 text-gray-400 mr-2" />
                        <h3 className="font-bold text-lg">{page.title}</h3>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Página: <span className="text-toctoc-blue font-medium">{getFriendlyPageName(page.page_name)}</span> • 
                        Última atualização: {page.updated_at ? new Date(page.updated_at).toLocaleString('pt-BR') : 'N/A'}
                      </p>
                      {page.meta_description && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                          <span className="font-medium">Meta Descrição:</span> {page.meta_description}
                        </div>
                      )}
                    </div>
                    <div className="mt-4 md:mt-0">
                      <Button
                        variant="outline"
                        onClick={() => handleEdit(page)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar Página
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="bg-gray-50 p-4 rounded-md whitespace-pre-line max-h-[400px] overflow-auto">
                      {page.content}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PagesModule;
